import React, { createContext, useContext, useState, useEffect } from "react";

// Create the context
export const AuthContext = createContext();

// Create the provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);

  // Load user data from localStorage on mount - with safe parsing
  useEffect(() => {
    try {
      const savedUser = localStorage.getItem("user");
      const savedToken = localStorage.getItem("token");
      const savedRole = localStorage.getItem("userRole");

      // Only parse if the value exists and is not 'undefined'
      if (
        savedUser &&
        savedUser !== "undefined" &&
        savedToken &&
        savedToken !== "undefined"
      ) {
        setUser(JSON.parse(savedUser));
        setToken(savedToken);
        setUserRole(savedRole || null);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error("Error loading user from localStorage:", error);
      // Clear invalid data
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      localStorage.removeItem("userRole");
    } finally {
      setLoading(false);
    }
  }, []);

  // Login function - stores response data
  const login = (userData, tokenData) => {
    try {
      setUser(userData);
      setToken(tokenData);
      setIsAuthenticated(true);

      // Extract role name from nested structure
      const roleName = userData?.role?.roleName || "DEFAULT";
      setUserRole(roleName);

      // Persist to localStorage
      localStorage.setItem("user", JSON.stringify(userData));
      localStorage.setItem("token", String(tokenData));
      localStorage.setItem("userRole", roleName);
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  };

  // Logout function - clears data
  const logout = () => {
    setUser(null);
    setToken(null);
    setUserRole(null);
    setIsAuthenticated(false);

    // Clear from localStorage
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("userRole");
  };

  // Get dashboard route based on role
  // Only MARKETING_ADMIN and SALES_REP have special routes
  // Everyone else goes to default admin dashboard
  const getDashboardRoute = () => {
    // if (userRole === "MARKETING_ADMIN") {
    //   return "/marketing/dashboard";
    // }
    // if (userRole === "SALES_REP") {
    //   return "/sales/dashboard";
    // }
    // Default for all other roles
    return "/admin/dashboard";
  };

  // Check if user has specific access permission
  const hasAccess = (permission) => {
    return user?.role?.access?.[permission] || false;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        isAuthenticated,
        userRole,
        login,
        logout,
        getDashboardRoute,
        hasAccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
